import { type Ancestors, type ChildNodeType, DAMNodeType, type TreeRootNode } from '@/types/tree';
import { type SelectedNode } from '@/context/GallerySelectionContext';

/*
 * Container narrowing over `types/tree.ts`, in `lib/` because it has no layer of its own to belong
 * to: `hooks/data/tree/useSortedFilteredChildren` and `components/home/favorites` both need it, and
 * neither is a browser-view component. It lived in `components/browser/` first, which meant the home
 * page's favorites imported its type guard from the browser view.
 *
 * Not in `types/tree.ts` because `toSelectedNode` bridges a tree node to the gallery selection, and
 * `types/` importing from `context/` is the wrong direction.
 */

/** A tree child already proven to be a folder or an album. */
export type ChildTreeNode = TreeRootNode & { type: ChildNodeType };

export const isChildNodeType = (type: DAMNodeType): type is ChildNodeType =>
  type === DAMNodeType.Folder || type === DAMNodeType.Album;

/**
 * `TreeRootNode.type` is a validated `DAMNodeType`, so `asset` is representable here even though
 * TreeController's child listing defaults to folders and albums. A stray asset child is dropped
 * rather than relabelled: the previous narrowing answered "anything that isn't an album is a
 * folder", which would have given an asset a folder chip, a folder drop target, and a context
 * menu offering New Folder and Upload to Folder.
 */
export const isChildTreeNode = (child: TreeRootNode): child is ChildTreeNode => isChildNodeType(child.type);

export const toSelectedNode = (child: ChildTreeNode): SelectedNode => ({
  id: child.node.id,
  name: child.node.name,
  slug: child.node.slug,
  type: child.type,
});

/**
 * A browsed folder's own id and full root-first name path, for callers (the children section's
 * upload menu items, DM-2518 follow-up) that need it without reading the sidebar tree's node
 * cache — which never holds a node shown only in that section.
 *
 * `ancestors` comes back leaf-first (nearest ancestor first) — the same convention the breadcrumb
 * reverses before display (gallery-layout.tsx) — so it has to be reversed here too, or a folder
 * two or more levels deep gets its middle path segments scrambled.
 */
export const buildParentPath = (
  nodeId: string | undefined,
  name: string | undefined,
  ancestors: Ancestors | undefined,
): { id: string; namePath: Array<string> } | undefined =>
  nodeId && name
    ? { id: nodeId, namePath: [...[...(ancestors ?? [])].reverse().map((ancestor) => ancestor.name), name] }
    : undefined;
